import React from 'react';

import { observer } from 'mobx-react';
import { CircularProgress } from '@mui/material';

import DataError from 'containers/Error/DataError';
import DrawerHeader from 'containers/Tasks/components/ActionsSchedulesTable/ScheduleDrawer/DrawerHeader';
import {
  ContentContainer,
  LoadingContainer,
} from 'containers/Tasks/components/ActionsSchedulesTable/ScheduleDrawer/styles';
import { RiskActionStatus } from 'utils/api/modules/risks';
import { useStore } from 'utils/hooks/store';

import ControlActionFields from './ControlActionFields';
import ControlActionFooter from './ControlActionFooter';
import EvidenceForm from './EvidenceForm';
import { StyledFullHeightDrawer } from './styles';
import { useRiskAction } from './useRiskAction';

interface ControlActionDrawerProps {
  actionId: string;
  onClose: () => void;
  onUpdate: () => void;
}

const ControlActionDrawer: React.FunctionComponent<ControlActionDrawerProps> = ({ actionId, onClose, onUpdate }) => {
  const { session } = useStore();
  const { data, failed, setData, reload } = useRiskAction(actionId);
  const titleRef = React.useRef<HTMLHeadingElement>(null);
  const loaded = Boolean(data);

  React.useEffect(() => {
    if (loaded) titleRef.current?.focus();
  }, [loaded, actionId]);

  const canWork =
    data?.status === RiskActionStatus.ToDo && data.assignedTo.uuid === session.user?.uuid && !session.user?.readOnly;

  return (
    <StyledFullHeightDrawer data-testid="control-action-drawer">
      <DrawerHeader title={data?.name ?? 'Control action'} onClose={onClose} titleRef={titleRef} />
      {!data && !failed && (
        <LoadingContainer>
          <CircularProgress />
        </LoadingContainer>
      )}
      {failed && <DataError />}
      {data && canWork && (
        <EvidenceForm
          key={data.uuid}
          action={data}
          onSubmitted={(next) => {
            setData(next);
            onUpdate();
          }}
          onConflict={() => {
            reload();
            onUpdate();
          }}
        />
      )}
      {data && !canWork && (
        <>
          <ContentContainer>
            <ControlActionFields action={data} />
          </ContentContainer>
          <ControlActionFooter action={data} />
        </>
      )}
    </StyledFullHeightDrawer>
  );
};

export default observer(ControlActionDrawer);
